import React, { useState, useEffect } from "react";
import { X, Settings, Clock, Globe, Monitor, Check, Grid3x3, Sparkles } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { backgroundPatterns } from "../../contexts/ThemeContext";

const SettingsPanel = ({ isOpen, onClose, isHomePage = false }: { isOpen: boolean; onClose?: () => void; isHomePage?: boolean }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const panelPositionClass = isMobile ? "inset-0 top-[60px]" : "top-[60px] right-0 bottom-0 w-80";
  const [showSeconds, setShowSeconds] = useState(false);
  const { currentTheme, setTheme, themes, backgroundPattern, setBackgroundPattern, isAnimated, setIsAnimated } = useTheme();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const listener = (e: any) => {
      if (typeof e?.detail === "boolean") setShowSeconds(e.detail);
    };
    window.addEventListener("timeFormatChanged", listener);
    return () => window.removeEventListener("timeFormatChanged", listener);
  }, []);

  const handleShowSecondsChange = (value: boolean) => {
    setShowSeconds(value);
    import("../../services/api").then(({ ApiService }) => ApiService.updateMyPreferences({ showSeconds: value }).catch(() => {}));
    window.dispatchEvent(new CustomEvent("timeFormatChanged", { detail: value }));
  };

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          .settings-container::-webkit-scrollbar {
            display: none;
          }
          .settings-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          
          .homepage-switch {
            background: transparent !important;
          }
          
          .toggle-switch {
            position: relative;
            display: inline-block;
            width: 44px;
            height: 24px;
          }
          
          .toggle-switch input {
            opacity: 0;
            width: 0;
            height: 0;
          }
          
          .toggle-slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #64748b;
            transition: 0.3s;
            border-radius: 24px;
          }
          
          .toggle-slider:before {
            position: absolute;
            content: "";
            height: 18px;
            width: 18px;
            left: 3px;
            bottom: 3px;
            background-color: white;
            transition: 0.3s;
            border-radius: 50%;
          }
          
          input:checked + .toggle-slider {
            background-color: #3b82f6;
          }
          
          input:checked + .toggle-slider:before {
            transform: translateX(20px);
          }
        `}
      </style>

      {isMobile && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden" onClick={onClose} />}

      {!isHomePage && <div className={`fixed z-49 bg-white ${panelPositionClass}`} />}

      <div className={`fixed z-50 ${panelPositionClass}`}>
        <div
          className={`flex flex-col h-full shadow-2xl w-full ${isHomePage ? "backdrop-blur-lg" : "bg-white"} ${
            isMobile ? "rounded-none p-0" : isHomePage ? "border border-white/25 p-1" : "border border-gray-200 p-1"
          }`}
          style={
            isHomePage
              ? {
                  background: `linear-gradient(135deg, ${currentTheme.colors.background.base}26, ${currentTheme.colors.background.via}33, ${currentTheme.colors.background.to}26)`,
                }
              : undefined
          }>
          <div className="flex-shrink-0 px-6" style={{ paddingTop: "11px", paddingBottom: "11px" }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center">
                    <Settings size={16} className="text-white" />
                  </div>
                </div>

                <div className="flex-1">
                  <h2 className={`text-sm font-semibold ${isHomePage ? "text-white" : "text-gray-900"}`}>Sistem Ayarları</h2>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {onClose && (
                  <button
                    onClick={onClose}
                    className={`p-1.5 rounded transition-colors ${
                      isHomePage
                        ? "text-gray-200 hover:text-white hover:bg-white/15"
                        : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    }`}
                    aria-label="Kapat">
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="settings-container flex-1 overflow-y-auto px-6 py-4 space-y-6">
            <div>
              <h3 className={`text-sm font-semibold mb-3 flex items-center ${isHomePage ? "text-white" : "text-gray-900"}`}>
                <Clock size={16} className="mr-2" />
                Saat Ayarları
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className={`text-sm font-medium ${isHomePage ? "text-white" : "text-gray-700"}`}>Saniye Gösterimi</span>
                    <span className={`text-xs ${isHomePage ? "text-gray-200" : "text-gray-500"}`}>
                      Saatte saniye bilgisini göster (14:30:23)
                    </span>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={showSeconds} onChange={(e) => handleShowSecondsChange(e.target.checked)} />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div
                  className={`rounded-lg p-3 ${isHomePage ? "bg-white/10 border border-white/20" : "bg-blue-50 border border-blue-200"}`}>
                  <div className="flex items-center space-x-2">
                    <Globe size={16} className={isHomePage ? "text-blue-200" : "text-blue-500"} />
                    <span className={`text-sm font-medium ${isHomePage ? "text-blue-100" : "text-blue-700"}`}>
                      Saat Dilimi: GMT+3 (Türkiye Saati)
                    </span>
                  </div>
                  <p className={`text-xs mt-1 ${isHomePage ? "text-blue-200" : "text-blue-600"}`}>
                    Tüm zaman gösterimleri Türkiye saat dilimine göre ayarlanmıştır.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className={`text-sm font-semibold mb-3 flex items-center ${isHomePage ? "text-white" : "text-gray-900"}`}>
                <Monitor size={16} className="mr-2" />
                Renk Temaları
              </h3>
              <div className="space-y-3">
                {themes.map((theme) => (
                  <div
                    key={theme.id}
                    onClick={() => setTheme(theme.id)}
                    className={`rounded-lg p-3 cursor-pointer transition-all duration-300 ${
                      currentTheme.id === theme.id
                        ? isHomePage
                          ? "bg-white/20 border-2 border-white/40 shadow-lg"
                          : "bg-blue-50 border-2 border-blue-400 shadow-lg"
                        : isHomePage
                        ? "bg-white/10 border border-white/20 hover:bg-white/15"
                        : "bg-gray-50 border border-gray-200 hover:bg-gray-100"
                    }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="flex space-x-1">
                          <div
                            className="w-6 h-6 rounded-full border-2 border-white/50 shadow-md"
                            style={{ backgroundColor: theme.colors.primary }}
                          />
                          <div
                            className="w-6 h-6 rounded-full border-2 border-white/50 shadow-md"
                            style={{ backgroundColor: theme.colors.secondary }}
                          />
                          <div
                            className="w-6 h-6 rounded-full border-2 border-white/50 shadow-md"
                            style={{ backgroundColor: theme.colors.accent }}
                          />
                        </div>
                        <div>
                          <div className={`text-sm font-semibold ${isHomePage ? "text-white" : "text-gray-900"}`}>{theme.name}</div>
                        </div>
                      </div>
                      {currentTheme.id === theme.id && (
                        <div
                          className={`flex items-center justify-center w-6 h-6 rounded-full ${
                            isHomePage ? "bg-white/30 text-white" : "bg-blue-500 text-white"
                          }`}>
                          <Check size={14} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className={`text-sm font-semibold mb-3 flex items-center ${isHomePage ? "text-white" : "text-gray-900"}`}>
                <Sparkles size={16} className="mr-2" />
                Arka Plan Ayarları
              </h3>

              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className={`text-sm font-medium ${isHomePage ? "text-white" : "text-gray-700"}`}>Hareketli Arka Plan</span>
                    <span className={`text-xs ${isHomePage ? "text-gray-200" : "text-gray-500"}`}>Arka plan animasyonlarını aç/kapat</span>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={isAnimated} onChange={(e) => setIsAnimated(e.target.checked)} />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="mb-2">
                <span className={`text-sm font-medium ${isHomePage ? "text-white" : "text-gray-700"}`}>Desen Seçimi</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {backgroundPatterns.map((pattern) => (
                  <div
                    key={pattern.id}
                    onClick={() => setBackgroundPattern(pattern.id as any)}
                    className={`rounded-lg p-3 cursor-pointer transition-all duration-300 ${
                      backgroundPattern === pattern.id
                        ? isHomePage
                          ? "bg-white/20 border-2 border-white/40 shadow-lg"
                          : "bg-blue-50 border-2 border-blue-400 shadow-lg"
                        : isHomePage
                        ? "bg-white/10 border border-white/20 hover:bg-white/15"
                        : "bg-gray-50 border border-gray-200 hover:bg-gray-100"
                    }`}>
                    <div className="flex items-center justify-between">
                      <div className={`text-sm font-semibold ${isHomePage ? "text-white" : "text-gray-900"}`}>{pattern.name}</div>
                      {backgroundPattern === pattern.id && (
                        <div
                          className={`flex items-center justify-center w-5 h-5 rounded-full ${
                            isHomePage ? "bg-white/30 text-white" : "bg-blue-500 text-white"
                          }`}>
                          <Check size={12} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 px-6 py-4">
            <button
              onClick={onClose}
              className={`w-full px-4 py-2 border rounded-lg transition-colors font-medium ${
                isHomePage
                  ? "text-white bg-white/15 border-white/25 hover:bg-white/20 backdrop-blur-sm"
                  : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
              }`}>
              Kapat
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPanel;
